import * as React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Value Display Components for Modern UI V2
 *
 * These components handle financial value display with:
 * - JetBrains Mono font for number clarity
 * - Semantic colors for gains/losses
 * - Trend indicators
 */

/**
 * CurrencyValue - Displays currency values with proper formatting
 * Uses JetBrains Mono for clear number rendering
 */
const CurrencyValue = React.forwardRef(({
  className,
  value,
  currency = "EUR",
  size = "base",
  showSign = false,
  colorize = false,
  compact = false, // Use compact format for large numbers (K, M, B)
  ...props
}, ref) => {
  const sizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
    "4xl": "text-4xl",
    "5xl": "text-5xl",
  };

  const formatValue = (val) => {
    if (val === null || val === undefined || isNaN(val)) {
      return "---.--";
    }

    const absValue = Math.abs(val);
    const sign = val >= 0 ? (showSign ? "+" : "") : "-";

    // Format with currency symbol
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...(compact && absValue >= 10000 ? { notation: "compact", compactDisplay: "short" } : {}),
    }).format(absValue);

    return sign + formatted;
  };

  const formatFullValue = (val) => {
    if (val === null || val === undefined || isNaN(val)) {
      return "---.--";
    }
    const absValue = Math.abs(val);
    const sign = val >= 0 ? (showSign ? "+" : "") : "-";
    return sign + new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absValue);
  };

  const colorClass = colorize
    ? value > 0
      ? "text-success"
      : value < 0
      ? "text-destructive"
      : "text-foreground"
    : "text-foreground";

  const formattedValue = formatValue(value);
  const fullValue = formatFullValue(value);
  // Show tooltip for compact mode OR for large values that might be truncated visually
  const needsTooltip = (compact && value !== null && Math.abs(value) >= 10000) ||
    (value !== null && Math.abs(value) >= 100000);

  return (
    <span
      ref={ref}
      className={cn(
        "font-mono tabular-nums font-semibold whitespace-nowrap",
        sizeClasses[size],
        colorClass,
        className
      )}
      title={needsTooltip ? fullValue : undefined}
      {...props}
    >
      {formattedValue}
    </span>
  );
});
CurrencyValue.displayName = "CurrencyValue";

/**
 * PercentValue - Displays percentage values with proper formatting
 * Includes optional trend indicator
 */
const PercentValue = React.forwardRef(({
  className,
  value,
  size = "sm",
  showSign = true,
  showTrend = false,
  colorize = true,
  decimals = 2,
  ...props
}, ref) => {
  const sizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
  };

  const iconSizeClasses = {
    xs: "h-3 w-3",
    sm: "h-3.5 w-3.5",
    base: "h-4 w-4",
    lg: "h-5 w-5",
    xl: "h-6 w-6",
  };

  const formatValue = (val) => {
    if (val === null || val === undefined || isNaN(val)) {
      return "--%";
    }

    const sign = val >= 0 && showSign ? "+" : "";
    return `${sign}${val.toFixed(decimals)}%`;
  };

  const colorClass = colorize
    ? value > 0
      ? "text-success"
      : value < 0
      ? "text-destructive"
      : "text-muted-foreground"
    : "text-foreground";

  const TrendIcon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;

  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 font-mono tabular-nums font-medium",
        sizeClasses[size],
        colorClass,
        className
      )}
      {...props}
    >
      {showTrend && <TrendIcon className={iconSizeClasses[size]} />}
      {formatValue(value)}
    </span>
  );
});
PercentValue.displayName = "PercentValue";

/**
 * ValueChange - Displays a value change with trend indicator and description
 * Combines currency/percent with optional descriptive text
 */
const ValueChange = React.forwardRef(({
  className,
  value,
  type = "percent", // "percent" | "currency"
  description,
  size = "sm",
  showTrend = true,
  ...props
}, ref) => {
  const isPositive = value > 0;
  const isNegative = value < 0;

  const colorClass = isPositive
    ? "text-success"
    : isNegative
    ? "text-destructive"
    : "text-muted-foreground";

  const iconSizeClasses = {
    xs: "h-3 w-3",
    sm: "h-3.5 w-3.5",
    base: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

  return (
    <div
      ref={ref}
      className={cn("flex items-center gap-1", colorClass, className)}
      {...props}
    >
      {showTrend && <TrendIcon className={iconSizeClasses[size]} />}
      {type === "percent" ? (
        <PercentValue value={value} size={size} colorize={false} showTrend={false} />
      ) : (
        <CurrencyValue value={value} size={size} colorize={false} showSign />
      )}
      {description && (
        <span className="text-muted-foreground font-sans">{description}</span>
      )}
    </div>
  );
});
ValueChange.displayName = "ValueChange";

/**
 * StatusBadge - Displays a status indicator with semantic colors
 */
const StatusBadge = React.forwardRef(({
  className,
  status = "neutral", // "success" | "warning" | "error" | "info" | "neutral"
  children,
  size = "sm",
  ...props
}, ref) => {
  const statusClasses = {
    success: "bg-success/10 text-success border-success/20",
    warning: "bg-warning/10 text-warning border-warning/20",
    error: "bg-destructive/10 text-destructive border-destructive/20",
    info: "bg-info/10 text-info border-info/20",
    neutral: "bg-muted text-muted-foreground border-border",
  };

  const sizeClasses = {
    xs: "text-xs px-1.5 py-0.5",
    sm: "text-xs px-2 py-0.5",
    base: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5",
  };

  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center rounded-md border font-medium",
        statusClasses[status],
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
});
StatusBadge.displayName = "StatusBadge";

/**
 * GainLossBadge - Specialized badge for gain/loss display
 * Automatically determines color based on value
 */
const GainLossBadge = React.forwardRef(({
  className,
  value,
  type = "percent",
  size = "sm",
  ...props
}, ref) => {
  const status = value > 0 ? "success" : value < 0 ? "error" : "neutral";

  return (
    <StatusBadge ref={ref} status={status} size={size} className={className} {...props}>
      {type === "percent" ? (
        <PercentValue value={value} size={size} colorize={false} showTrend={false} />
      ) : (
        <CurrencyValue value={value} size={size} colorize={false} showSign />
      )}
    </StatusBadge>
  );
});
GainLossBadge.displayName = "GainLossBadge";

/**
 * AnimatedValue - Wrapper for adding entrance animations to values
 */
const AnimatedValue = React.forwardRef(({
  className,
  children,
  delay = 0,
  animation = "slide-up", // "fade-in" | "slide-up" | "scale-in"
  ...props
}, ref) => {
  const animationClasses = {
    "fade-in": "animate-fade-in",
    "slide-up": "animate-slide-up",
    "scale-in": "animate-scale-in",
  };

  const delayClasses = {
    0: "animation-delay-0",
    75: "animation-delay-75",
    100: "animation-delay-100",
    150: "animation-delay-150",
    200: "animation-delay-200",
    300: "animation-delay-300",
    400: "animation-delay-400",
    500: "animation-delay-500",
    700: "animation-delay-700",
    1000: "animation-delay-1000",
  };

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        animationClasses[animation],
        delayClasses[delay] || "animation-delay-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
AnimatedValue.displayName = "AnimatedValue";

export {
  CurrencyValue,
  PercentValue,
  ValueChange,
  StatusBadge,
  GainLossBadge,
  AnimatedValue,
};
